// EssayWorkspace — simulação de redação dentro de "Estudando com IA".
//
// Três fases:
//   1. setup     — aluno define tema, banca, timer, limite, fragmentos
//   2. running   — aluno escreve com cronômetro + contador (+ pausa)
//   3. submitted — IA corrigiu, mostra grading
//
// O timer mora num signal local que tick a cada 1s. Pausa/resume vai pro server,
// que mantém o totalPausedMs canônico (cliente reconcilia ao recarregar).

import { signal } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  FileText,
  Image as ImageIcon,
  Loader2,
  Pause,
  Play,
  Plus,
  Send,
  Sparkles,
  Table as TableIcon,
  Trash2,
  X
} from "lucide-react";
import {
  api,
  type AiStudySessionDetail,
  type EssayFragment,
  type EssayMeta
} from "./api";
import { AiMarkdownBlock } from "./aiMarkdown";
import { SessionHeaderActions, activeSessionId, activeAiStudySession } from "./AiStudyView";
import { openConfirm } from "./ConfirmPromptModal";
import {
  computeEquivalentLines,
  describeHandwriting,
  describePreset,
  essayCutoffIndex,
  HANDWRITING_SIZES,
  LINE_PRESETS
} from "./essayLines";
import { EssayCadernoPanel } from "./EssayCadernoPanel";
import { renderEssayMarkdown } from "@app/ui/lib/essay-markdown";
import { aiCorrectionPrefs } from "./aiPrefs";
import { fireCorrectAnimation } from "./animations";

const _tick = signal(0);

// =============================================================================
// Helpers
// =============================================================================

/** Indentação visual de início de parágrafo — 2 tabs (renderizados pela CSS
 *  `tab-size: 4` do textarea, ou seja ~8 colunas). Aplicado no início do
 *  texto e em cada Enter. */
const PARAGRAPH_INDENT = "\t\t";

function essayPhase(meta: EssayMeta | null): "setup" | "running" | "paused" | "submitted" {
  if (!meta) return "setup";
  if (meta.submittedAt) return "submitted";
  if (!meta.startedAt) return "setup";
  if (meta.pausedAt) return "paused";
  return "running";
}

function elapsedMs(meta: EssayMeta | null, nowMs: number): number {
  if (!meta?.startedAt) return 0;
  const startMs = Date.parse(meta.startedAt);
  const baseElapsed = nowMs - startMs;
  const pausedExtra = meta.pausedAt ? nowMs - Date.parse(meta.pausedAt) : 0;
  return Math.max(0, baseElapsed - (meta.totalPausedMs ?? 0) - pausedExtra);
}

function fmtMs(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h${String(m).padStart(2, "0")}m${String(s).padStart(2, "0")}s`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}
/** Conta caracteres EXCLUINDO o indent de parágrafo. O indent (espaços
 *  líderes de cada linha após \n) é só formatação visual — não conta
 *  como caractere "digitado" pelo aluno nem como palavra. */
function countTypedChars(text: string): number {
  return (
    text.length -
    text.split("\n").reduce((sum, line) => {
      const m = line.match(/^\s+/);
      return sum + (m ? m[0].length : 0);
    }, 0)
  );
}
function countLines(text: string): number {
  return text.split(/\n/).length;
}

const BANCAS = [
  "ENEM",
  "FUVEST",
  "UNICAMP",
  "UNESP",
  "ITA",
  "PUC",
  "Vestibular geral",
  "Concurso público",
  "Personalizado"
] as const;

// =============================================================================
// Componente raiz
// =============================================================================

export function EssayWorkspace({
  data,
  onExit,
  onChange
}: {
  data: AiStudySessionDetail;
  onExit: () => void;
  onChange: () => Promise<void> | void;
}) {
  // ticker pra cronômetro
  useEffect(() => {
    const t = setInterval(() => {
      _tick.value++;
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const meta = data.session.essayMeta;
  const phase = essayPhase(meta);

  return (
    <div class="flex h-[calc(100vh-10rem)] flex-col gap-3">
      <EssayHeader data={data} onExit={onExit} onChange={onChange} />
      {phase === "setup" && (
        <EssaySetup data={data} onChange={onChange} />
      )}
      {(phase === "running" || phase === "paused") && (
        <EssayRunning data={data} onChange={onChange} />
      )}
      {phase === "submitted" && <EssayResult data={data} />}
    </div>
  );
}

function EssayHeader({
  data,
  onExit,
  onChange
}: {
  data: AiStudySessionDetail;
  onExit: () => void;
  onChange: () => Promise<void> | void;
}) {
  const meta = data.session.essayMeta;
  const phase = essayPhase(meta);
  return (
    <header class="flex items-start justify-between gap-3 rounded-md border border-border bg-card/40 px-3 py-2">
      <div class="min-w-0 flex-1">
        <button
          type="button"
          onClick={onExit}
          class="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3" /> Voltar
        </button>
        <h1 class="mt-1 line-clamp-1 text-lg font-bold tracking-tight">{data.session.title}</h1>
        <div class="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
          <span class="rounded-md bg-violet-500/10 px-1.5 py-0.5 font-semibold text-violet-700 dark:text-violet-300">
            Redação
          </span>
          {meta?.banca && <span>Banca: {meta.banca}</span>}
          <span>Fase: {phase}</span>
        </div>
      </div>
      <SessionHeaderActions
        sessionId={data.session.id}
        title={data.session.title}
        theme={data.session.theme}
        kind="essay"
        onExit={onExit}
        onRenamed={() => void onChange()}
      />
    </header>
  );
}

// =============================================================================
// Setup
// =============================================================================

function EssaySetup({
  data,
  onChange
}: {
  data: AiStudySessionDetail;
  onChange: () => Promise<void> | void;
}) {
  const meta = data.session.essayMeta ?? {};
  const [banca, setBanca] = useState<string>(meta.banca ?? "Vestibular geral");
  const [customPersona, setCustomPersona] = useState(meta.customPersona ?? "");
  const [textosApoio, setTextosApoio] = useState(meta.textosApoio ?? "");
  const [direcionadores, setDirecionadores] = useState(meta.direcionadores ?? "");
  const [timerMode, setTimerMode] = useState<"none" | "countdown">(meta.timerMode ?? "none");
  const [durationMin, setDurationMin] = useState<number>(meta.durationMin ?? 90);
  const [maxKind, setMaxKind] = useState<"words" | "chars" | "lines">(meta.maxKind ?? "lines");
  const [maxValue, setMaxValue] = useState<number>(meta.maxValue ?? 300);
  // Linhas equivalentes manuscritas
  const [linePreset, setLinePreset] = useState<NonNullable<EssayMeta["linePreset"]>>(
    meta.linePreset ?? "enem"
  );
  const [maxLines, setMaxLines] = useState<number>(meta.maxLines ?? 30);
  const [charsPerLineBase, setCharsPerLineBase] = useState<number>(
    meta.charsPerLineBase ?? 70
  );
  const [handwritingSize, setHandwritingSize] = useState<NonNullable<EssayMeta["handwritingSize"]>>(
    meta.handwritingSize ?? "medium"
  );
  const [calibratedCharsPerLine, setCalibratedCharsPerLine] = useState<number | null>(
    meta.calibratedCharsPerLine ?? null
  );

  // Configurações novas — caderno realista, idioma, penalidades.
  // [[project-caderno-realista]]
  const [acceptedLanguages, setAcceptedLanguages] = useState<Array<"pt" | "en" | "es">>(
    meta.acceptedLanguages ?? ["pt"]
  );
  const [cadernoLanguage, setCadernoLanguage] = useState<"pt" | "en" | "es">(
    meta.cadernoLanguage ?? "pt"
  );
  const [requireTitle, setRequireTitle] = useState<boolean>(meta.cadernoRequireTitle ?? false);
  const [penaltySpelling, setPenaltySpelling] = useState<boolean>(
    meta.cadernoPenalties?.spelling ?? true
  );
  const [penaltyParagraphs, setPenaltyParagraphs] = useState<boolean>(
    meta.cadernoPenalties?.paragraphs ?? true
  );
  const [penaltyLanguage, setPenaltyLanguage] = useState<boolean>(
    meta.cadernoPenalties?.language ?? true
  );

  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Carrega "minha letra" do user.prefs pra usar como default caso o aluno
  // ainda não tenha calibrado nesta sessão.
  useEffect(() => {
    if (calibratedCharsPerLine != null) return; // já vem do meta
    (async () => {
      try {
        const r = await api.getPrefs();
        const myChars = (r.prefs as any)?.essayMyCharsPerLine;
        if (typeof myChars === "number" && myChars >= 20) {
          setCalibratedCharsPerLine(myChars);
        }
      } catch {}
    })();
  }, []);

  async function saveMeta() {
    setSaving(true);
    setErr(null);
    try {
      await api.patchEssayMeta(data.session.id, {
        banca: banca as any,
        customPersona: customPersona.trim() || null,
        textosApoio: textosApoio.trim() || null,
        direcionadores: direcionadores.trim() || null,
        timerMode,
        durationMin: timerMode === "countdown" ? durationMin : null,
        maxKind,
        maxValue: maxValue > 0 ? maxValue : null,
        linePreset,
        maxLines: linePreset === "custom" ? maxLines : null,
        charsPerLineBase: linePreset === "custom" || linePreset === "free" ? charsPerLineBase : null,
        handwritingSize,
        calibratedCharsPerLine: handwritingSize === "calibrated" ? calibratedCharsPerLine : null,
        // Caderno realista
        acceptedLanguages,
        cadernoLanguage,
        cadernoRequireTitle: requireTitle,
        cadernoPenalties: {
          spelling: penaltySpelling,
          paragraphs: penaltyParagraphs,
          language: penaltyLanguage
        }
      });
      await onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function startNow() {
    await saveMeta();
    setStarting(true);
    setErr(null);
    try {
      await api.startEssay(data.session.id);
      await onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }

  return (
    <div class="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-y-auto lg:grid-cols-2">
      {/* Coluna 1: tema + persona + texto/direcionadores */}
      <div class="space-y-3">
        <SectionCard title="Tema (definido na criação)" icon={FileText}>
          <div class="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
            {data.session.theme}
          </div>
        </SectionCard>

        <SectionCard title="Banca / Persona" icon={CheckCircle2}>
          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Banca
            </span>
            <select
              value={banca}
              onChange={(e) => setBanca((e.target as HTMLSelectElement).value)}
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            >
              {BANCAS.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </label>
          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Persona específica (opcional — sobrescreve banca)
            </span>
            <input
              type="text"
              value={customPersona}
              onInput={(e) => setCustomPersona((e.target as HTMLInputElement).value)}
              maxLength={500}
              placeholder="Ex: corretor severo do cursinho X"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </label>
        </SectionCard>

        <SectionCard title="Textos de apoio (opcional)" icon={FileText}>
          <textarea
            value={textosApoio}
            onInput={(e) => setTextosApoio((e.target as HTMLTextAreaElement).value)}
            rows={6}
            placeholder="Cole aqui textos de apoio (Texto I, II, III...). Pra cada um, separe com uma linha em branco."
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-xs outline-none focus:border-primary"
          />
        </SectionCard>

        <SectionCard title="Direcionadores ao corretor (opcional)" icon={CheckCircle2}>
          <textarea
            value={direcionadores}
            onInput={(e) => setDirecionadores((e.target as HTMLTextAreaElement).value)}
            rows={3}
            placeholder="Ex: 'considere que é uma dissertação-argumentativa nos moldes do ENEM com proposta de intervenção'."
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-xs outline-none focus:border-primary"
          />
        </SectionCard>
      </div>

      {/* Coluna 2: timer + limite + fragmentos */}
      <div class="space-y-3">
        <SectionCard title="Tempo de execução" icon={Clock}>
          <div class="flex flex-wrap gap-2">
            <RadioPill
              active={timerMode === "none"}
              onClick={() => setTimerMode("none")}
              title="Livre"
              desc="Cronômetro progressivo, sem limite"
            />
            <RadioPill
              active={timerMode === "countdown"}
              onClick={() => setTimerMode("countdown")}
              title="Com tempo"
              desc="Cronômetro regressivo até zerar"
            />
          </div>
          {timerMode === "countdown" && (
            <label class="block">
              <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Duração (min)
              </span>
              <input
                type="number"
                min="5"
                max="600"
                value={durationMin}
                onInput={(e) => setDurationMin(Number((e.target as HTMLInputElement).value))}
                class="w-32 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              />
            </label>
          )}
        </SectionCard>

        <SectionCard title="Linhas equivalentes (estilo manuscrito)" icon={FileText}>
          <p class="text-[11px] text-muted-foreground">
            Como na folha de redação à mão: cada banca tem N linhas e ~N chars/linha.
            O tamanho da sua letra altera quantos caracteres cabem por linha.
          </p>

          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Preset / banca
            </span>
            <select
              value={linePreset}
              onChange={(e) =>
                setLinePreset((e.target as HTMLSelectElement).value as NonNullable<EssayMeta["linePreset"]>)
              }
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              title="Preset / banca"
            >
              {LINE_PRESETS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label} — {p.desc}
                </option>
              ))}
            </select>
          </label>

          {linePreset === "custom" && (
            <div class="grid grid-cols-2 gap-2">
              <label class="block">
                <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Linhas máx.
                </span>
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={maxLines}
                  onInput={(e) => setMaxLines(Number((e.target as HTMLInputElement).value))}
                  class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </label>
              <label class="block">
                <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Chars/linha (letra média)
                </span>
                <input
                  type="number"
                  min="20"
                  max="150"
                  value={charsPerLineBase}
                  onInput={(e) =>
                    setCharsPerLineBase(Number((e.target as HTMLInputElement).value))
                  }
                  class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </label>
            </div>
          )}

          <HandwritingPicker
            value={handwritingSize}
            onChange={setHandwritingSize}
            onCalibrated={(chars) => {
              setHandwritingSize("calibrated");
              setCalibratedCharsPerLine(chars);
            }}
            calibratedChars={calibratedCharsPerLine}
          />

          <PresetPreview
            linePreset={linePreset}
            maxLines={maxLines}
            charsPerLineBase={charsPerLineBase}
            handwritingSize={handwritingSize}
            calibratedCharsPerLine={calibratedCharsPerLine}
          />
        </SectionCard>

        <SectionCard title="Limite extra (palavras/caracteres, opcional)" icon={FileText}>
          <p class="text-[11px] text-muted-foreground">
            Limite adicional caso queira controlar também por palavras ou caracteres.
            Deixe em 0 pra ignorar.
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <select
              value={maxKind}
              onChange={(e) => setMaxKind((e.target as HTMLSelectElement).value as any)}
              class="rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              title="Tipo de limite extra"
            >
              <option value="words">Palavras</option>
              <option value="chars">Caracteres</option>
              <option value="lines">Linhas equivalentes</option>
            </select>
            <input
              type="number"
              min="0"
              max="50000"
              value={maxValue}
              onInput={(e) => setMaxValue(Number((e.target as HTMLInputElement).value))}
              title="Valor do limite extra"
              placeholder="0"
              class="w-32 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
            <span class="text-[11px] text-muted-foreground">0 = sem limite</span>
          </div>
        </SectionCard>

        <SectionCard title="Fragmentos de apoio (texto/imagem/tabela)" icon={ImageIcon}>
          <FragmentsList
            sessionId={data.session.id}
            fragments={data.fragments ?? []}
            onChange={onChange}
          />
        </SectionCard>
      </div>

      {/* Caderno realista — idioma + penalidades + título obrigatório */}
      <div class="col-span-full rounded-lg border border-border bg-card p-4">
        <h3 class="mb-3 flex items-center gap-1.5 text-sm font-bold">
          <Sparkles className="size-3.5 text-violet-500" />
          Regras de correção
        </h3>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Idioma que vou escrever
            </label>
            <div class="flex gap-2">
              {(["pt", "en", "es"] as const).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setCadernoLanguage(lang)}
                  class={`flex-1 rounded-md border px-3 py-1.5 text-xs font-semibold ${
                    cadernoLanguage === lang
                      ? "border-violet-500 bg-violet-500/10 text-violet-700 dark:text-violet-300"
                      : "border-border bg-background hover:border-violet-500/60"
                  }`}
                >
                  {lang === "pt" ? "Português" : lang === "en" ? "Inglês" : "Espanhol"}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Idiomas que o corretor aceita
            </label>
            <div class="flex gap-2">
              {(["pt", "en", "es"] as const).map((lang) => {
                const checked = acceptedLanguages.includes(lang);
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => {
                      setAcceptedLanguages((prev) =>
                        prev.includes(lang)
                          ? prev.filter((l) => l !== lang)
                          : [...prev, lang]
                      );
                    }}
                    class={`flex-1 rounded-md border px-3 py-1.5 text-xs font-semibold ${
                      checked
                        ? "border-violet-500 bg-violet-500/10 text-violet-700 dark:text-violet-300"
                        : "border-border bg-background hover:border-violet-500/60"
                    }`}
                  >
                    {lang === "pt" ? "Português" : lang === "en" ? "Inglês" : "Espanhol"}
                  </button>
                );
              })}
            </div>
            <p class="mt-1 text-[10px] text-muted-foreground">
              Escrever em idioma fora da lista zera (regra das bancas) — pode desligar abaixo.
            </p>
          </div>
        </div>

        <div class="mt-4 space-y-2">
          <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Penalidades do corretor
          </p>
          {[
            {
              key: "spelling",
              label: "Descontar nota por erros ortográficos",
              checked: penaltySpelling,
              set: setPenaltySpelling
            },
            {
              key: "paragraphs",
              label: "Descontar nota se faltar estrutura em parágrafos",
              checked: penaltyParagraphs,
              set: setPenaltyParagraphs
            },
            {
              key: "language",
              label: "ZERAR redação que estiver em idioma fora dos aceitos",
              checked: penaltyLanguage,
              set: setPenaltyLanguage
            }
          ].map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => p.set(!p.checked)}
              class="flex w-full items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-left hover:bg-muted/40"
            >
              <span class="text-xs">{p.label}</span>
              <div
                class={`h-4 w-7 shrink-0 rounded-full transition-colors ${
                  p.checked ? "bg-violet-500" : "bg-muted"
                }`}
              >
                <div
                  class={`h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    p.checked ? "translate-x-3" : "translate-x-0"
                  }`}
                />
              </div>
            </button>
          ))}
        </div>

        <div class="mt-4">
          <button
            type="button"
            onClick={() => setRequireTitle(!requireTitle)}
            class="flex w-full items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-left hover:bg-muted/40"
          >
            <span class="text-xs">
              <strong>Exigir título</strong> — linha 0 acima do texto (FUVEST exige; ENEM não)
            </span>
            <div
              class={`h-4 w-7 shrink-0 rounded-full transition-colors ${
                requireTitle ? "bg-violet-500" : "bg-muted"
              }`}
            >
              <div
                class={`h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                  requireTitle ? "translate-x-3" : "translate-x-0"
                }`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Footer: ações */}
      <div class="col-span-full flex flex-wrap items-center justify-end gap-2">
        {err && (
          <span class="mr-auto text-xs text-destructive">
            <AlertCircle className="mr-1 inline size-3" /> {err}
          </span>
        )}
        <button
          type="button"
          onClick={() => void saveMeta()}
          disabled={saving}
          class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50"
        >
          {saving ? <Loader2 className="size-4 animate-spin" /> : null}
          Salvar configuração
        </button>
        <button
          type="button"
          onClick={() => void startNow()}
          disabled={starting}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {starting ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
          Começar redação
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Fragmentos (no setup)
// =============================================================================

function FragmentsList({
  sessionId,
  fragments,
  onChange
}: {
  sessionId: string;
  fragments: EssayFragment[];
  onChange: () => Promise<void> | void;
}) {
  const [adding, setAdding] = useState(false);

  async function remove(fragId: string) {
    const ok = await openConfirm({
      title: "Remover fragmento?",
      message: "Este texto/imagem/tabela de apoio será removido da proposta.",
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    await api.deleteEssayFragment(sessionId, fragId);
    await onChange();
  }

  return (
    <div class="space-y-2">
      {fragments.length === 0 ? (
        <p class="text-xs text-muted-foreground">
          Nenhum fragmento ainda. Cada fragmento pode combinar texto, imagem e
          tabela — adicione os elementos que se relacionam num só bloco.
        </p>
      ) : (
        <ul class="space-y-2">
          {fragments.map((f) => {
            const hasText = !!f.textContent;
            const hasImage = !!f.imageDataUrl;
            const hasTable = !!f.tableData;
            return (
              <li
                key={f.id}
                class="group flex items-start gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-xs"
              >
                <FragmentBadge hasText={hasText} hasImage={hasImage} hasTable={hasTable} />
                <div class="min-w-0 flex-1 space-y-1">
                  <div class="flex flex-wrap items-baseline gap-2">
                    <strong class="font-bold">
                      {f.label || `Fragmento ${f.position + 1}`}
                    </strong>
                    <FragmentTypeLabel
                      hasText={hasText}
                      hasImage={hasImage}
                      hasTable={hasTable}
                    />
                  </div>
                  {hasImage && (
                    <img
                      src={f.imageDataUrl!}
                      alt={f.label}
                      class="max-h-32 rounded-md border border-border"
                    />
                  )}
                  {hasText && (
                    <p class="line-clamp-3 whitespace-pre-wrap text-[11px] text-muted-foreground">
                      {f.textContent}
                    </p>
                  )}
                  {hasTable && f.tableData && <TableMini data={f.tableData} />}
                </div>
                <button
                  type="button"
                  onClick={() => void remove(f.id)}
                  class="text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
                  title="Remover"
                >
                  <Trash2 className="size-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        onClick={() => setAdding(true)}
        class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-[11px] font-semibold hover:bg-accent"
      >
        <Plus className="size-3" /> Adicionar fragmento
      </button>

      {adding && (
        <AddFragmentModal
          sessionId={sessionId}
          onClose={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await onChange();
          }}
        />
      )}
    </div>
  );
}

function FragmentBadge({
  hasText,
  hasImage,
  hasTable
}: {
  hasText: boolean;
  hasImage: boolean;
  hasTable: boolean;
}) {
  return (
    <div class="mt-0.5 flex shrink-0 flex-col items-center gap-0.5 text-violet-500">
      {hasImage && <ImageIcon className="size-3" />}
      {hasTable && <TableIcon className="size-3" />}
      {hasText && <FileText className="size-3" />}
      {!hasText && !hasImage && !hasTable && <FileText className="size-3" />}
    </div>
  );
}

function FragmentTypeLabel({
  hasText,
  hasImage,
  hasTable
}: {
  hasText: boolean;
  hasImage: boolean;
  hasTable: boolean;
}) {
  const parts = [
    hasText && "texto",
    hasImage && "imagem",
    hasTable && "tabela"
  ].filter(Boolean) as string[];
  if (parts.length === 0) return null;
  return (
    <span class="text-[10px] uppercase tracking-wider text-muted-foreground">
      {parts.join(" + ")}
    </span>
  );
}

function TableMini({ data }: { data: { headers: string[]; rows: string[][] } }) {
  return (
    <div class="mt-1 max-h-24 overflow-auto rounded-md border border-border">
      <table class="w-full text-[10px]">
        <thead class="bg-muted/40">
          <tr>
            {data.headers.map((h, i) => (
              <th key={i} class="border-r border-border px-1.5 py-0.5 text-left">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.slice(0, 3).map((row, i) => (
            <tr key={i} class="border-t border-border">
              {row.map((c, j) => (
                <td key={j} class="border-r border-border px-1.5 py-0.5">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddFragmentModal({
  sessionId,
  onClose,
  onAdded
}: {
  sessionId: string;
  onClose: () => void;
  onAdded: () => Promise<void> | void;
}) {
  const [label, setLabel] = useState("");
  const [text, setText] = useState("");
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [imageCaption, setImageCaption] = useState("");
  const [tableHeaders, setTableHeaders] = useState("Coluna A\tColuna B");
  const [tableRows, setTableRows] = useState("");
  const [includeText, setIncludeText] = useState(true);
  const [includeImage, setIncludeImage] = useState(false);
  const [includeTable, setIncludeTable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function pickImage(e: Event) {
    const target = e.target as HTMLInputElement;
    const f = target.files?.[0];
    if (!f) return;
    if (f.size > 1_500_000) {
      setErr("Imagem muito grande (máx 1.5MB). Comprima antes.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageDataUrl(String(reader.result));
    reader.readAsDataURL(f);
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      // Texto consolidado do fragmento: o texto principal + (se imagem) a
      // legenda/descrição + (se tabela) a anotação. Tudo num só textContent
      // pra a IA correlacionar.
      const textParts: string[] = [];
      if (includeText && text.trim()) textParts.push(text.trim());
      const payload: any = { label: label.trim() || undefined };
      if (includeImage) {
        if (!imageDataUrl) throw new Error("Selecione uma imagem ou desligue a seção de imagem.");
        payload.imageDataUrl = imageDataUrl;
        if (imageCaption.trim()) textParts.push(`[Descrição da imagem]\n${imageCaption.trim()}`);
      }
      let tableData: { headers: string[]; rows: string[][] } | undefined;
      if (includeTable) {
        const headers = tableHeaders
          .split(/\t|;|,/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (headers.length === 0) throw new Error("Cabeçalhos da tabela obrigatórios.");
        const rows = tableRows
          .split("\n")
          .map((r) => r.split(/\t|;|,/).map((s) => s.trim()))
          .filter((r) => r.some((c) => c));
        if (rows.length === 0) throw new Error("Tabela precisa de pelo menos uma linha.");
        tableData = { headers, rows };
        payload.tableData = tableData;
      }
      payload.textContent = textParts.join("\n\n");

      if (!includeText && !includeImage && !includeTable) {
        throw new Error("Habilite ao menos um conteúdo: texto, imagem ou tabela.");
      }
      if (includeText && !text.trim() && !payload.imageDataUrl && !payload.tableData) {
        throw new Error("Adicione algum conteúdo no fragmento.");
      }
      await api.addEssayFragment(sessionId, payload);
      await onAdded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <div>
            <h2 class="text-lg font-bold">Novo fragmento</h2>
            <p class="text-[11px] text-muted-foreground">
              Um fragmento pode combinar texto, imagem e tabela quando os três se
              relacionam. Ative as seções que vão compor este fragmento.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            class="text-muted-foreground hover:text-foreground"
            title="Fechar"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>

        <div class="space-y-3">
          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Rótulo (opcional)
            </span>
            <input
              type="text"
              value={label}
              onInput={(e) => setLabel((e.target as HTMLInputElement).value)}
              maxLength={200}
              placeholder="Ex: Texto I — Guimarães Rosa"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </label>

          <SectionToggle
            icon={FileText}
            label="Texto"
            enabled={includeText}
            onToggle={() => setIncludeText((v) => !v)}
          >
            <textarea
              value={text}
              onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
              rows={6}
              placeholder="Trecho de texto que faz parte deste fragmento."
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </SectionToggle>

          <SectionToggle
            icon={ImageIcon}
            label="Imagem"
            enabled={includeImage}
            onToggle={() => setIncludeImage((v) => !v)}
          >
            <input
              type="file"
              accept="image/*"
              onChange={pickImage}
              class="w-full text-sm"
              title="Selecione uma imagem (máx 1.5MB)"
              aria-label="Selecione uma imagem (máx 1.5MB)"
            />
            {imageDataUrl && (
              <img
                src={imageDataUrl}
                alt="Preview"
                class="mt-2 max-h-48 rounded-md border border-border"
              />
            )}
            <label class="mt-2 block">
              <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Legenda / descrição (a IA usa pra correlacionar com o texto)
              </span>
              <textarea
                value={imageCaption}
                onInput={(e) => setImageCaption((e.target as HTMLTextAreaElement).value)}
                rows={2}
                placeholder="Descreva o que a imagem mostra."
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              />
            </label>
          </SectionToggle>

          <SectionToggle
            icon={TableIcon}
            label="Tabela"
            enabled={includeTable}
            onToggle={() => setIncludeTable((v) => !v)}
          >
            <label class="block">
              <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Cabeçalhos (Tab, vírgula ou ponto-e-vírgula)
              </span>
              <input
                type="text"
                value={tableHeaders}
                onInput={(e) => setTableHeaders((e.target as HTMLInputElement).value)}
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-xs outline-none focus:border-primary"
              />
            </label>
            <label class="mt-2 block">
              <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Linhas (uma por linha, separadas por Tab/vírgula)
              </span>
              <textarea
                value={tableRows}
                onInput={(e) => setTableRows((e.target as HTMLTextAreaElement).value)}
                rows={5}
                placeholder={"Linha 1 col A\tLinha 1 col B\nLinha 2 col A\tLinha 2 col B"}
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-xs outline-none focus:border-primary"
              />
            </label>
          </SectionToggle>

          {err && (
            <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {err}
            </div>
          )}
          <div class="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white hover:brightness-110 disabled:opacity-50"
            >
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              Adicionar fragmento
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionToggle({
  icon: Icon,
  label,
  enabled,
  onToggle,
  children
}: {
  icon: any;
  label: string;
  enabled: boolean;
  onToggle: () => void;
  children: any;
}) {
  return (
    <div
      class={`rounded-md border ${
        enabled ? "border-primary/30 bg-primary/5" : "border-border bg-background"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        class="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span class="flex items-center gap-1.5 text-xs font-bold">
          <Icon className={`size-3.5 ${enabled ? "text-primary" : "text-muted-foreground"}`} />
          {label}
        </span>
        <span
          class={`text-[10px] font-bold uppercase tracking-wider ${
            enabled ? "text-primary" : "text-muted-foreground"
          }`}
        >
          {enabled ? "Incluído" : "Adicionar"}
        </span>
      </button>
      {enabled && <div class="border-t border-border/60 px-3 pb-3 pt-2">{children}</div>}
    </div>
  );
}

// =============================================================================
// Running / Paused
// =============================================================================

function EssayRunning({
  data,
  onChange
}: {
  data: AiStudySessionDetail;
  onChange: () => Promise<void> | void;
}) {
  // metaState local pra atualização otimista das configs do caderno (settings
  // popover); commita pro server em background via patchEssayMeta debounced.
  const [metaState, setMetaState] = useState<EssayMeta | null>(
    data.session.essayMeta ?? null
  );
  // Sincroniza com prop se mudar de fora (ex: reload da sessão).
  useEffect(() => {
    setMetaState(data.session.essayMeta ?? null);
  }, [data.session.id, data.session.essayMeta]);
  const meta = metaState;

  const [content, setContent] = useState(data.session.essayContent ?? "");
  const [title, setTitle] = useState(meta?.essayTitle ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [busyAction, setBusyAction] = useState<"pause" | "resume" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const metaDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Patch debounced de essayMeta (configs do caderno + título)
  function patchMetaDebounced(patch: Partial<EssayMeta>) {
    setMetaState((prev) => ({ ...(prev ?? {}), ...patch }) as EssayMeta);
    if (metaDebounceRef.current) clearTimeout(metaDebounceRef.current);
    metaDebounceRef.current = setTimeout(() => {
      void api.patchEssayMeta(data.session.id, patch).catch((e) => {
        console.error("[essayMeta patch]", e);
      });
    }, 800);
  }
  // Autosave: status mostra "salvando…", "salvo HH:MM", ou "erro".
  const [autosaveStatus, setAutosaveStatus] = useState<
    "idle" | "dirty" | "saving" | "saved" | "error"
  >("idle");
  const [autosavedAt, setAutosavedAt] = useState<string | null>(
    data.session.essayContent ? data.session.updatedAt ?? null : null
  );
  const lastSavedContentRef = useRef<string>(data.session.essayContent ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // _tick subscription pra forçar render do timer
  const _ = _tick.value;
  const nowMs = Date.now();
  const elapsed = elapsedMs(meta, nowMs);
  const isPaused = !!meta?.pausedAt;

  const isCountdown = !!(meta?.timerMode === "countdown" && meta.durationMin);
  const totalMs = isCountdown ? meta!.durationMin! * 60_000 : 0;
  const remainingMs = isCountdown ? Math.max(0, totalMs - elapsed) : 0;
  const timeUp = isCountdown && remainingMs === 0;

  const wordCount = countWords(content);
  const lineCount = countLines(content);
  const charCount = countTypedChars(content);
  const maxValue = meta?.maxValue ?? 0;
  const maxKind: "words" | "chars" | "lines" = (meta?.maxKind as any) ?? "lines";

  // Inicia conteúdo com o indent de parágrafo se sessão nova/vazia.
  useEffect(() => {
    if (!content) setContent(PARAGRAPH_INDENT);
  }, []);

  // Autosave debounced (~1.5s após parar de digitar) — protege o trabalho do
  // aluno se ele fechar o browser, perder conexão, etc.
  useEffect(() => {
    if (isPaused || timeUp || submitting) return;
    if (content === lastSavedContentRef.current) return;
    setAutosaveStatus("dirty");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setAutosaveStatus("saving");
        const r = await api.autosaveEssayContent(data.session.id, content);
        lastSavedContentRef.current = content;
        setAutosavedAt(r.savedAt);
        setAutosaveStatus("saved");
      } catch (e) {
        setAutosaveStatus("error");
        console.error("[autosave]", e);
      }
    }, 1500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [content, isPaused, timeUp, submitting]);

  // Flush imediato quando aluno sai da aba ou pausa
  useEffect(() => {
    function flush() {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (content === lastSavedContentRef.current) return;
      // Best-effort: navigator.sendBeacon não dá pra usar com PATCH simples,
      // então usamos fetch keepalive (suportado em browsers modernos).
      try {
        fetch(
          `/api/student/ai-study?id=${encodeURIComponent(data.session.id)}&action=essay_content`,
          {
            method: "PATCH",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ content }),
            keepalive: true
          }
        ).catch(() => {});
      } catch {}
    }
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
    };
  }, [content, data.session.id]);

  async function pause() {
    setBusyAction("pause");
    try {
      await api.pauseEssay(data.session.id);
      await onChange();
    } finally {
      setBusyAction(null);
    }
  }
  async function resume() {
    setBusyAction("resume");
    try {
      await api.resumeEssay(data.session.id);
      await onChange();
    } finally {
      setBusyAction(null);
    }
  }
  async function submit() {
    // Validação de título quando a banca exige (FUVEST/UNICAMP/ITA). O
    // aluno pode entregar sem título mesmo assim — só precisa confirmar
    // explicitamente que está ciente da penalidade.
    if (meta?.themeImposedTitle && !title.trim()) {
      const ok = await openConfirm({
        title: "Entregar sem título?",
        message:
          "Esta prova exige título por norma da banca, e o campo está vazio. A IA corretora descontará por ausência conforme a regra do vestibular. Quer entregar assim mesmo?",
        confirmLabel: "Entregar sem título",
        danger: true
      });
      if (!ok) return;
    }
    if (!content.trim() || content.trim().length < 50) {
      const ok = await openConfirm({
        title: "Entregar mesmo curta?",
        message: "A redação parece muito curta (menos de 50 caracteres). A IA pode não conseguir avaliar competências completas.",
        confirmLabel: "Entregar assim mesmo",
        danger: true
      });
      if (!ok) return;
    } else {
      const ok = await openConfirm({
        title: "Entregar a redação?",
        message: "Após a entrega, a IA fará a correção e a redação ficará bloqueada para edição.",
        confirmLabel: "Entregar para correção"
      });
      if (!ok) return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await api.submitEssay(data.session.id, content);
      await onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  // Auto-submit quando tempo zerar
  useEffect(() => {
    if (timeUp && !submitting && meta?.startedAt && !meta?.submittedAt && !isPaused) {
      void submit();
    }
  }, [timeUp]);

  /** Dentro do textarea:
   *   - Enter → insere `\n` + 2 tabs (novo parágrafo já indentado)
   *   - Tab   → insere 1 tab (não tira foco do textarea)
   *   - Shift+Tab → ignorado (deixa o default, que sai do textarea)
   *  Usa setRangeText pra evitar race entre re-render do Preact e o cursor. */
  function onKeyDown(e: KeyboardEvent) {
    const ta = e.target as HTMLTextAreaElement;
    if (!ta || typeof ta.setRangeText !== "function") return;
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      ta.setRangeText(`\n${PARAGRAPH_INDENT}`, ta.selectionStart, ta.selectionEnd, "end");
      setContent(ta.value);
      return;
    }
    if (e.key === "Tab" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      ta.setRangeText("\t", ta.selectionStart, ta.selectionEnd, "end");
      setContent(ta.value);
      return;
    }
  }

  const fragmentsForPanel = data.fragments ?? [];
  const hasCaderno =
    fragmentsForPanel.length > 0 ||
    !!meta?.officialInstructions ||
    !!meta?.statement ||
    (Array.isArray(meta?.directives) && (meta?.directives as string[]).length > 0);

  return (
    <div class="relative flex min-h-0 flex-1 flex-col gap-2">
      {submitting && <AnalyzingOverlay />}
      {/* Barra superior: timer + contadores + ações */}
      <div class={`flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 ${
        isPaused
          ? "border-amber-500/40 bg-amber-500/5"
          : timeUp
            ? "border-red-500/40 bg-red-500/10"
            : "border-border bg-card/40"
      }`}>
        <div class="flex items-center gap-1.5">
          <Clock className={`size-4 ${timeUp ? "text-red-600" : isPaused ? "text-amber-600" : "text-primary"}`} />
          <span class="text-sm font-bold tabular-nums">
            {isCountdown ? fmtMs(remainingMs) : fmtMs(elapsed)}
          </span>
          <span class="text-[10px] uppercase tracking-wider text-muted-foreground">
            {isCountdown ? "restante" : "tempo livre"}
          </span>
        </div>
        <RunningCounters
          content={content}
          meta={meta}
          wordCount={wordCount}
          charCount={charCount}
          maxKind={maxKind}
          maxValue={maxValue}
        />
        <AutosaveIndicator status={autosaveStatus} savedAt={autosavedAt} />
        <div class="ml-auto flex items-center gap-2">
          {isPaused ? (
            <button
              type="button"
              onClick={() => void resume()}
              disabled={busyAction !== null}
              class="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
            >
              {busyAction === "resume" ? <Loader2 className="size-3 animate-spin" /> : <Play className="size-3" />}
              Retomar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void pause()}
              disabled={busyAction !== null || timeUp}
              class="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-bold text-amber-700 hover:bg-amber-500/20 dark:text-amber-300 disabled:opacity-50"
            >
              {busyAction === "pause" ? <Loader2 className="size-3 animate-spin" /> : <Pause className="size-3" />}
              Pausar
            </button>
          )}
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting || isPaused}
            class="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3" />}
            Encerrar e corrigir
          </button>
        </div>
      </div>

      {err && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mr-1 inline size-3" /> {err}
        </div>
      )}

      {/* Editor + caderno lateral */}
      <div class="flex min-h-0 flex-1 gap-2">
        <div class="flex min-h-0 flex-1 flex-col gap-2">
          {/* Linha 0 — Título (quando exigido pela banca/proposta). Não tem
              numeração, sem indent, limitada a 1 linha de charsPerLine.
              Quando o tema da biblioteca impõe (FUVEST/UNICAMP/ITA), o campo
              vira obrigatório de verdade: badge "Obrigatório", borda
              destacada em vermelho se vazio e bloqueio no submit. */}
          {meta?.cadernoRequireTitle && (
            <div
              class={`rounded-md border bg-[#fdfcf7] px-3 py-2 dark:bg-stone-900/40 ${
                meta.themeImposedTitle && !title.trim()
                  ? "border-red-400/70 dark:border-red-500/60"
                  : "border-border"
              }`}
            >
              <label class="block">
                <span class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500">
                  Título
                  {meta.themeImposedTitle && (
                    <span class="rounded bg-red-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-red-700 dark:text-red-300">
                      Obrigatório
                    </span>
                  )}
                </span>
                <input
                  type="text"
                  value={title}
                  onInput={(e) => {
                    const v = (e.target as HTMLInputElement).value;
                    // Trava em 1 linha do mesmo tamanho do texto
                    const cpl = meta?.cadernoCharsPerLine ?? meta?.charsPerLineBase ?? 80;
                    const clamped = v.slice(0, cpl).replace(/\n/g, " ");
                    setTitle(clamped);
                    patchMetaDebounced({ essayTitle: clamped });
                  }}
                  disabled={isPaused || timeUp || submitting}
                  maxLength={meta?.cadernoCharsPerLine ?? meta?.charsPerLineBase ?? 80}
                  placeholder={
                    meta.themeImposedTitle
                      ? "Esta prova exige um título — escreva aqui antes de enviar"
                      : "Dê um título à sua redação"
                  }
                  required={meta.themeImposedTitle}
                  class="w-full border-0 border-b border-stone-300/50 bg-transparent py-1 text-base outline-none focus:border-violet-500 disabled:opacity-50 dark:border-stone-700/50"
                />
              </label>
              {meta.themeImposedTitle && !title.trim() && (
                <p class="mt-1 text-[10px] text-red-600 dark:text-red-400">
                  Esta prova exige título. A IA corretora descontará por
                  ausência conforme a norma da banca.
                </p>
              )}
            </div>
          )}

          <PlainEssayTextarea
            value={content}
            onChange={setContent}
            disabled={isPaused || timeUp || submitting}
            onKeyDown={onKeyDown}
            content={content}
            meta={meta}
          />

          {isPaused && (
            <div class="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
              <Pause className="mr-1 inline size-3" />
              Redação pausada. O cronômetro está parado. Clique em <strong>Retomar</strong>.
            </div>
          )}
        </div>

        {hasCaderno && (
          <EssayCadernoPanel
            sessionId={data.session.id}
            meta={meta}
            fragments={fragmentsForPanel}
            title={data.session.title || data.session.theme || "Caderno"}
            viewMode={meta?.cadernoView ?? "side"}
            onViewModeChange={(v) => patchMetaDebounced({ cadernoView: v })}
          />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Área de digitação — textarea liso, sem pautas/numeração visuais.
// As métricas (linhas equiv., palavras, chars, tempo) vivem no header, e
// um banner discreto avisa quando passou do limite de linhas equivalentes.
// =============================================================================

function PlainEssayTextarea({
  value,
  onChange,
  disabled,
  onKeyDown,
  content,
  meta
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  onKeyDown?: (e: KeyboardEvent) => void;
  content: string;
  meta: EssayMeta | null;
}) {
  const eq = computeEquivalentLines(content, meta);
  const overBy = eq.maxLines != null ? Math.max(0, eq.realized - eq.maxLines) : 0;
  return (
    <div class="relative flex min-h-0 flex-1 flex-col">
      <textarea
        value={value}
        onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
        onKeyDown={onKeyDown as any}
        disabled={disabled}
        spellcheck
        placeholder="Comece a escrever sua redação."
        class="min-h-[60vh] flex-1 resize-none rounded-md border border-border bg-[#fdfcf7] px-5 py-4 text-base leading-relaxed text-foreground outline-none [tab-size:4] focus:border-primary disabled:opacity-50 dark:bg-stone-900/40"
      />
      {overBy > 0 && (
        <div class="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1 text-[11px] font-semibold text-red-700 shadow-sm dark:text-red-300">
          {overBy} linha{overBy === 1 ? "" : "s"} além do limite — não entram na correção
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Resultado da correção
// =============================================================================

function EssayResult({ data }: { data: AiStudySessionDetail }) {
  const g = data.session.essayGrading;
  const meta = data.session.essayMeta ?? null;
  const fragments = data.fragments ?? [];
  const content = data.session.essayContent ?? "";
  const [duplicating, setDuplicating] = useState(false);
  const [dupErr, setDupErr] = useState<string | null>(null);

  // Celebra quando a nota aparece pela primeira vez nesta sessão E está
  // acima do threshold do aluno E o toggle de celebração de redação está
  // ativo. Usa ref pra disparar só uma vez por session.id.
  const celebratedRef = useRef<string | null>(null);
  useEffect(() => {
    const sid = data.session.id;
    if (!g || typeof g.score !== "number") return;
    if (celebratedRef.current === sid) return;
    const prefs = aiCorrectionPrefs.value;
    if (!prefs.celebrateEssay) {
      celebratedRef.current = sid;
      return;
    }
    if (g.score >= prefs.threshold) {
      try {
        fireCorrectAnimation();
      } catch {
        // ignora
      }
    }
    celebratedRef.current = sid;
  }, [data.session.id, g?.score]);

  async function duplicate() {
    const ok = await openConfirm({
      title: "Iniciar uma nova redação a partir desta?",
      message:
        "A nova sessão começa pré-preenchida com tema, banca, persona, direcionadores, textos de apoio e fragmentos desta redação. Você pode alterar qualquer coisa antes de começar.\n\nNão tem vínculo com esta — é vida nova.",
      confirmLabel: "Criar nova redação"
    });
    if (!ok) return;
    setDuplicating(true);
    setDupErr(null);
    try {
      const r = await api.duplicateEssay(data.session.id);
      // Sai do workspace atual e abre a nova sessão
      activeSessionId.value = r.id;
      activeAiStudySession.value = { id: r.id, title: data.session.title, kind: "essay" };
    } catch (e) {
      setDupErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDuplicating(false);
    }
  }

  // body é o conteúdo sem a linha NOTA_FINAL — fallback pra content em sessões antigas
  const body = g?.body ?? g?.content ?? "";

  // Métricas realizadas
  const wordCount = countWords(content);
  const lineCount = countLines(content);
  const charCount = countTypedChars(content);
  const startedMs = meta?.startedAt ? Date.parse(meta.startedAt) : null;
  const submittedMs = meta?.submittedAt ? Date.parse(meta.submittedAt) : null;
  const totalPausedMs = meta?.totalPausedMs ?? 0;
  const activeMs = startedMs && submittedMs ? submittedMs - startedMs - totalPausedMs : null;

  return (
    <div class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      {g && <GradeStrip grading={g} />}

      <div class="flex flex-wrap items-center justify-end gap-2">
        {dupErr && (
          <span class="mr-auto text-xs text-destructive">
            <AlertCircle className="mr-1 inline size-3" /> {dupErr}
          </span>
        )}
        <button
          type="button"
          onClick={() => void duplicate()}
          disabled={duplicating}
          class="inline-flex items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs font-bold text-violet-700 hover:bg-violet-500/20 dark:text-violet-300 disabled:opacity-50"
          title="Cria uma nova redação pré-preenchida com tudo desta. Vida nova, sem vínculo."
        >
          {duplicating ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Plus className="size-3" />
          )}
          Iniciar nova redação a partir desta
        </button>
      </div>

      <div class="grid min-h-0 grid-cols-1 gap-3 lg:grid-cols-[1fr_1.2fr]">
        {/* Coluna esquerda: contexto + redação */}
        <div class="space-y-3">
          <SectionCard title="Contexto da redação" icon={FileText}>
            <div class="space-y-2 text-xs">
              <ContextItem label="Tema">{data.session.theme}</ContextItem>
              {meta?.banca && <ContextItem label="Banca">{meta.banca}</ContextItem>}
              {meta?.customPersona && (
                <ContextItem label="Persona específica">{meta.customPersona}</ContextItem>
              )}
              {meta?.direcionadores && (
                <ContextItem label="Direcionadores ao corretor">
                  {meta.direcionadores}
                </ContextItem>
              )}
              {meta?.textosApoio && (
                <ContextItem label="Textos de apoio (texto livre)">
                  <pre class="whitespace-pre-wrap text-[11px]">{meta.textosApoio}</pre>
                </ContextItem>
              )}
            </div>
          </SectionCard>

          <SectionCard title="Sua redação (entregue)" icon={FileText}>
            <EssayBody content={content} meta={meta} />
            <ResultStats
              meta={meta}
              content={content}
              wordCount={wordCount}
              lineCount={lineCount}
              charCount={charCount}
              activeMs={activeMs}
            />
            {submittedMs && (
              <p class="mt-2 text-[10px] text-muted-foreground">
                Entregue em {new Date(submittedMs).toLocaleString("pt-BR")}
              </p>
            )}
          </SectionCard>

          {fragments.length > 0 && (
            <SectionCard title={`Fragmentos de apoio (${fragments.length})`} icon={ImageIcon}>
              <ul class="space-y-2">
                {fragments.map((f) => (
                  <FragmentReadOnly key={f.id} fragment={f} />
                ))}
              </ul>
            </SectionCard>
          )}

          <SectionCard title="Regras × Execução" icon={Clock}>
            <RulesVsExecution
              meta={meta}
              content={content}
              wordCount={wordCount}
              charCount={charCount}
              lineCount={lineCount}
              activeMs={activeMs}
              totalPausedMs={totalPausedMs}
            />
          </SectionCard>
        </div>

        {/* Coluna direita: correção */}
        <SectionCard title="Análise do corretor (IA)" icon={CheckCircle2}>
          {g ? (
            <>
              <AiMarkdownBlock text={body} className="text-foreground/95" />
              <p class="mt-3 border-t border-border pt-2 text-[10px] text-muted-foreground">
                Corrigido por {g.provider} ({g.model}) em{" "}
                {new Date(g.gradedAt).toLocaleString("pt-BR")}
              </p>
            </>
          ) : (
            <p class="text-sm text-muted-foreground">
              <Loader2 className="mr-1 inline size-3 animate-spin" />
              Aguardando correção…
            </p>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function ContextItem({ label, children }: { label: string; children: any }) {
  return (
    <div class="rounded-md border border-border bg-muted/20 px-3 py-2">
      <div class="mb-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div class="text-xs leading-relaxed">{children}</div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div class="rounded-md bg-muted/30 px-2 py-1.5">
      <div class="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div class="text-sm font-bold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

/** Renderiza um fragmento (texto/imagem/tabela/composite) em modo só-leitura.
 *  Usa o MESMO tratamento visual do painel lateral do EssayCadernoPanel:
 *  rótulo em uppercase tracking-wider, corpo em essay-paper, fonte
 *  (markdown) renderizada abaixo em itálico. Continuidade visual entre
 *  escrita e revisão. [[project-caderno-realista]] */
function FragmentReadOnly({ fragment: f }: { fragment: EssayFragment }) {
  const hasText = !!f.textContent;
  const hasImage = !!f.imageDataUrl;
  const hasTable = !!f.tableData;
  return (
    <li class="rounded-md border border-border bg-background px-3 py-2">
      <div class="mb-1.5 flex flex-wrap items-center gap-2 border-b border-border/40 pb-1.5">
        <FileText className="size-3.5 text-stone-600 dark:text-stone-300" />
        <strong class="text-[11px] font-bold uppercase tracking-wider text-stone-600 dark:text-stone-300">
          {f.label || `Fragmento ${f.position + 1}`}
        </strong>
        <FragmentTypeLabel
          hasText={hasText}
          hasImage={hasImage}
          hasTable={hasTable}
        />
      </div>
      <div class="space-y-2 pt-2">
        {hasImage && (
          <figure>
            <img
              src={f.imageDataUrl!}
              alt={f.label}
              class="mx-auto max-h-60 rounded-md border border-border"
            />
            {f.textContent && !hasTable && (
              <figcaption class="mt-1 text-center text-[11px] italic text-muted-foreground">
                {f.textContent}
              </figcaption>
            )}
          </figure>
        )}
        {hasText && !hasImage && (
          <div
            class="essay-paper text-[13px]"
            dangerouslySetInnerHTML={{
              __html: renderEssayMarkdown(f.textContent)
            }}
          />
        )}
        {hasTable && f.tableData && (
          <div class="overflow-x-auto">
            <table class="w-full border-collapse text-[12px]">
              <thead>
                <tr>
                  {f.tableData.headers.map((h, i) => (
                    <th
                      key={i}
                      class="border border-border bg-muted/50 px-2 py-1 text-left font-semibold"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {f.tableData.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((c, ci) => (
                      <td
                        key={ci}
                        class="border border-border px-2 py-1 align-top"
                      >
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {f.source && (
          <p
            class="text-[11px] italic text-muted-foreground"
            dangerouslySetInnerHTML={{
              __html: /^(fonte|font|f\.)\s*:/i.test(f.source.trim())
                ? renderEssayMarkdown(f.source.trim())
                : "<strong>Fonte:</strong> " +
                  renderEssayMarkdown(f.source.trim())
            }}
          />
        )}
      </div>
    </li>
  );
}

/** Render do corpo da redação no MESMO papel pautado em que foi escrita —
 *  mantém o feel de "caderno entregue" também na tela de resultado. Usa as
 *  configs do meta (chars/linha, tamanho da letra, fonte, numeração) pra
 *  bater visualmente com o que o aluno viu durante a escrita. */
/** Render da redação entregue — texto liso, fonte Inter, sem pautas/numeração.
 *  Os chars que ultrapassaram o limite saem em VERMELHO (e não foram enviados
 *  pra IA — regra das bancas espelhada pelo helper de truncamento). */
function EssayBody({
  content,
  meta
}: {
  content: string;
  meta: EssayMeta | null;
}) {
  if (!content.trim()) return <p class="text-sm text-muted-foreground">—</p>;
  const { cutoff, droppedLines } = essayCutoffIndex(content, meta);
  const within = content.slice(0, cutoff);
  const over = content.slice(cutoff);
  return (
    <div class="space-y-2">
      <div class="whitespace-pre-wrap rounded-md border border-border bg-[#fdfcf7] px-5 py-4 text-base leading-relaxed text-foreground [tab-size:4] dark:bg-stone-900/40">
        <span>{within}</span>
        {over && (
          <span class="text-red-600 line-through decoration-red-500/40 dark:text-red-400">
            {over}
          </span>
        )}
      </div>
      {droppedLines > 0 && (
        <p class="text-[11px] text-red-600 dark:text-red-400">
          {droppedLines} linha{droppedLines === 1 ? "" : "s"} além do limite —
          marcadas em vermelho, NÃO foram enviadas pra correção da IA.
        </p>
      )}
    </div>
  );
}

/** Overlay incisivo enquanto a IA está corrigindo a redação. A análise pode
 *  levar 30–60s; sem feedback claro, o aluno pensa que a tela travou. */
function AnalyzingOverlay() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);
  const messages = [
    "Lendo sua redação…",
    "Identificando tese e argumentação…",
    "Avaliando coesão e norma culta…",
    "Comparando com critérios da banca…",
    "Calculando notas por competência…",
    "Preparando sugestões de reescrita…",
    "Finalizando o veredito…"
  ];
  const messageIdx = Math.min(Math.floor(elapsed / 8), messages.length - 1);
  return (
    <div class="absolute inset-0 z-30 flex items-center justify-center rounded-md bg-background/90 backdrop-blur-sm">
      <div class="mx-4 w-full max-w-md rounded-lg border border-violet-500/40 bg-card p-6 text-center shadow-xl">
        <div class="relative mx-auto mb-4 size-16">
          <div class="absolute inset-0 animate-ping rounded-full bg-violet-500/30" />
          <div class="absolute inset-0 grid place-items-center rounded-full bg-violet-500/15">
            <Sparkles className="size-7 text-violet-600 dark:text-violet-300" />
          </div>
        </div>
        <h2 class="mb-1 text-lg font-bold tracking-tight">IA corrigindo sua redação</h2>
        <p class="mb-3 text-xs text-muted-foreground">
          Isso pode levar entre 30 e 60 segundos. Não feche esta tela.
        </p>
        <div class="space-y-2 rounded-md border border-border bg-muted/30 px-3 py-2.5">
          <div class="flex items-center justify-center gap-2 text-xs font-semibold">
            <Loader2 className="size-3.5 animate-spin text-violet-500" />
            <span>{messages[messageIdx]}</span>
          </div>
          <div class="text-[10px] tabular-nums text-muted-foreground">
            {String(Math.floor(elapsed / 60)).padStart(2, "0")}:
            {String(elapsed % 60).padStart(2, "0")} decorridos
          </div>
        </div>
        <p class="mt-3 text-[10px] text-muted-foreground">
          Sua redação já foi salva no servidor. Se algo der errado, ela está
          preservada.
        </p>
      </div>
    </div>
  );
}

function ResultStats({
  meta,
  content,
  wordCount,
  lineCount,
  charCount,
  activeMs
}: {
  meta: EssayMeta | null;
  content: string;
  wordCount: number;
  lineCount: number;
  charCount: number;
  activeMs: number | null;
}) {
  const eq = computeEquivalentLines(content, meta);
  return (
    <div class="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-2 text-[10px] text-muted-foreground sm:grid-cols-6">
      <Stat
        label="Linhas equiv."
        value={eq.maxLines != null ? `${eq.realized}/${eq.maxLines}` : String(eq.realized)}
      />
      <Stat
        label="Chars/linha"
        value={`~${eq.effectiveCharsPerLine}`}
      />
      <Stat label="Palavras" value={String(wordCount)} />
      <Stat label="Linhas (quebras)" value={String(lineCount)} />
      <Stat label="Caracteres" value={String(charCount)} />
      <Stat label="Tempo ativo" value={activeMs != null ? fmtMs(activeMs) : "—"} />
    </div>
  );
}

function RunningCounters({
  content,
  meta,
  wordCount,
  charCount,
  maxKind,
  maxValue
}: {
  content: string;
  meta: EssayMeta | null;
  wordCount: number;
  charCount: number;
  maxKind: "words" | "chars" | "lines";
  maxValue: number;
}) {
  const eq = computeEquivalentLines(content, meta);
  const linesMax = eq.maxLines;
  const linesPct = linesMax ? eq.realized / linesMax : 0;
  const linesTone =
    linesMax == null
      ? "text-foreground"
      : eq.realized > linesMax
        ? "font-bold text-red-600 dark:text-red-400"
        : linesPct >= 0.85
          ? "font-bold text-amber-600 dark:text-amber-400"
          : "font-bold text-emerald-600 dark:text-emerald-400";

  // Limite extra (palavras / chars / linhas) — separado do limite principal de linhas.
  let extraOver = false;
  let extraText: string | null = null;
  if (maxValue > 0) {
    const realized =
      maxKind === "words" ? wordCount : maxKind === "chars" ? charCount : eq.realized;
    extraOver = realized > maxValue;
    extraText = `limite extra: ${maxValue} ${
      maxKind === "words" ? "palavras" : maxKind === "chars" ? "chars" : "linhas"
    }`;
  }

  return (
    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      {/* Linhas equivalentes — destaque principal */}
      <div class="inline-flex items-center gap-1">
        <span class={`text-sm tabular-nums ${linesTone}`}>
          {eq.realized}
          {linesMax != null && <span class="text-muted-foreground"> / {linesMax}</span>}
        </span>
        <span class="text-[10px] uppercase tracking-wider">linhas</span>
      </div>
      <span class="text-muted-foreground/40">·</span>
      <span>
        <strong class="text-foreground">{wordCount}</strong> palavras
      </span>
      <span>
        <strong class="text-foreground">{charCount}</strong> chars
      </span>
      {extraText && (
        <span class={extraOver ? "font-bold text-red-600" : ""}>
          · {extraText}
        </span>
      )}
    </div>
  );
}

function PresetPreview({
  linePreset,
  maxLines,
  charsPerLineBase,
  handwritingSize,
  calibratedCharsPerLine
}: {
  linePreset: NonNullable<EssayMeta["linePreset"]>;
  maxLines: number;
  charsPerLineBase: number;
  handwritingSize: NonNullable<EssayMeta["handwritingSize"]>;
  calibratedCharsPerLine: number | null;
}) {
  const eq = computeEquivalentLines("", {
    linePreset,
    maxLines,
    charsPerLineBase,
    handwritingSize,
    calibratedCharsPerLine
  });
  return (
    <p class="rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-[11px] text-violet-700 dark:text-violet-300">
      <strong>Resumo:</strong> {eq.maxLines ?? "∞"} linhas máx · ~{eq.effectiveCharsPerLine}{" "}
      chars/linha (letra {describeHandwriting(handwritingSize)}). Capacidade total: ~
      {eq.maxLines ? eq.maxLines * eq.effectiveCharsPerLine : "ilimitada"} chars.
    </p>
  );
}

// ============================================================================
// HandwritingPicker — 5 níveis + opção "calibrada" com modal de medição
// ============================================================================

function HandwritingPicker({
  value,
  onChange,
  onCalibrated,
  calibratedChars
}: {
  value: NonNullable<EssayMeta["handwritingSize"]>;
  onChange: (v: NonNullable<EssayMeta["handwritingSize"]>) => void;
  onCalibrated: (chars: number) => void;
  calibratedChars: number | null;
}) {
  const [showCalibration, setShowCalibration] = useState(false);
  return (
    <div>
      <div class="mb-1 flex items-baseline justify-between gap-2">
        <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Tamanho da sua letra (autodeclarado)
        </span>
        <button
          type="button"
          onClick={() => setShowCalibration(true)}
          class="text-[11px] font-semibold text-primary hover:underline"
        >
          {calibratedChars
            ? `Recalibrar (atual: ${calibratedChars} chars/linha)`
            : "Calibrar minha letra"}
        </button>
      </div>
      <select
        value={value}
        onChange={(e) =>
          onChange((e.target as HTMLSelectElement).value as NonNullable<EssayMeta["handwritingSize"]>)
        }
        class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        title="Tamanho da letra"
      >
        {HANDWRITING_SIZES.map((h) => (
          <option key={h.key} value={h.key} disabled={h.key === "calibrated" && !calibratedChars}>
            {h.label} — {h.hint}
            {h.key === "calibrated" && calibratedChars ? ` (${calibratedChars}/linha)` : ""}
            {h.key === "calibrated" && !calibratedChars ? " — calibre primeiro" : ""}
          </option>
        ))}
      </select>
      {showCalibration && (
        <CalibrationModal
          initialChars={calibratedChars}
          onClose={() => setShowCalibration(false)}
          onDone={(chars) => {
            onCalibrated(chars);
            setShowCalibration(false);
          }}
        />
      )}
    </div>
  );
}

/** Pede pro aluno digitar uma frase que ele saiba ocupar exatamente 1 linha
 *  manuscrita (sem ser de parágrafo novo). Conta os chars e retorna como
 *  `chars/linha` pessoal. Vai pro user.prefs.essayMyCharsPerLine via savePrefs. */
function CalibrationModal({
  initialChars,
  onClose,
  onDone
}: {
  initialChars: number | null;
  onClose: () => void;
  onDone: (chars: number) => void;
}) {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const charCount = phrase.trim().length;

  async function save() {
    if (charCount < 20 || charCount > 200) {
      setErr("A frase precisa ter entre 20 e 200 caracteres.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // Mescla com prefs existentes (savePrefs substitui o objeto inteiro)
      const cur = await api.getPrefs();
      const merged = { ...(cur.prefs ?? {}), essayMyCharsPerLine: charCount };
      await api.savePrefs(merged);
      onDone(charCount);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class="w-full max-w-lg rounded-lg border border-border bg-card p-6 shadow-xl">
        <div class="mb-3 flex items-center justify-between">
          <h2 class="text-lg font-bold">Calibrar minha letra</h2>
          <button
            type="button"
            onClick={onClose}
            class="text-muted-foreground hover:text-foreground"
            title="Fechar"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        <p class="mb-3 text-xs text-muted-foreground">
          Em uma folha de redação, escreva à mão uma <strong>linha cheia</strong>{" "}
          (que tomou a largura toda da linha, sem ser linha de parágrafo). Depois
          <strong> digite aqui o que você escreveu</strong> — vamos contar os
          caracteres e usar isso como o tamanho da sua linha.
        </p>
        <textarea
          value={phrase}
          onInput={(e) => setPhrase((e.target as HTMLTextAreaElement).value)}
          rows={3}
          maxLength={300}
          placeholder="Digite aqui a frase que ocupou 1 linha inteira na folha…"
          class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        />
        <div class="mt-2 text-[11px] text-muted-foreground">
          <strong class="text-foreground tabular-nums">{charCount}</strong> caracteres
          {initialChars && (
            <>
              {" · "}
              <span>atual salvo: {initialChars}/linha</span>
            </>
          )}
        </div>
        {err && (
          <div class="mt-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy || charCount < 20}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            Salvar minha letra
          </button>
        </div>
        <p class="mt-3 text-[10px] text-muted-foreground">
          Esta medida fica salva no seu perfil — pode ser usada em qualquer redação
          futura selecionando "Minha letra (calibrada)" no seletor de tamanho.
        </p>
      </div>
    </div>
  );
}

function AutosaveIndicator({
  status,
  savedAt
}: {
  status: "idle" | "dirty" | "saving" | "saved" | "error";
  savedAt: string | null;
}) {
  if (status === "idle") return null;
  if (status === "saving" || status === "dirty") {
    return (
      <span class="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
        <Loader2 className="size-2.5 animate-spin" />
        Salvando…
      </span>
    );
  }
  if (status === "error") {
    return (
      <span class="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600 dark:text-red-400">
        <AlertCircle className="size-2.5" />
        Falha ao salvar
      </span>
    );
  }
  const time = savedAt
    ? new Date(savedAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : "—";
  return (
    <span class="inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400">
      <CheckCircle2 className="size-2.5" />
      Salvo às {time}
    </span>
  );
}

function RulesVsExecution({
  meta,
  content,
  wordCount,
  charCount,
  lineCount,
  activeMs,
  totalPausedMs
}: {
  meta: EssayMeta | null;
  content: string;
  wordCount: number;
  charCount: number;
  lineCount: number;
  activeMs: number | null;
  totalPausedMs: number;
}) {
  const timerMode = meta?.timerMode ?? "none";
  const durationMin = meta?.durationMin ?? null;
  const maxKind = meta?.maxKind ?? "words";
  const maxValue = meta?.maxValue ?? 0;
  const allowPause = !!meta?.allowPause;
  const lineEq = computeEquivalentLines(content, meta);

  const rows: Array<{ label: string; config: any; done: any }> = [
    {
      label: "Cronômetro",
      config:
        timerMode === "countdown" && durationMin
          ? `Regressivo · ${durationMin} min`
          : "Livre (progressivo)",
      done: activeMs != null ? fmtMs(activeMs) : "—"
    }
  ];
  if (timerMode === "countdown" && durationMin) {
    const totalAllowed = durationMin * 60_000;
    const used = activeMs != null ? Math.min(activeMs, totalAllowed) : 0;
    const remaining = activeMs != null ? Math.max(0, totalAllowed - activeMs) : totalAllowed;
    rows.push({
      label: "Tempo restante ao encerrar",
      config: fmtMs(totalAllowed),
      done: `${fmtMs(used)} usados · ${fmtMs(remaining)} restantes`
    });
  }
  rows.push({
    label: "Pausa permitida",
    config: allowPause ? "Sim" : "Não",
    done: totalPausedMs > 0 ? `Total em pausa: ${fmtMs(totalPausedMs)}` : "Sem pausa"
  });

  // Linhas equivalentes (limite principal)
  const linesWithin = lineEq.maxLines == null || lineEq.realized <= lineEq.maxLines;
  rows.push({
    label: "Linhas equivalentes (manuscrito)",
    config:
      lineEq.preset === "free"
        ? `Sem limite · ~${lineEq.effectiveCharsPerLine} chars/linha (letra ${describeHandwriting(lineEq.handwritingSize)})`
        : `${describePreset(lineEq.preset)} · ${lineEq.maxLines} linhas × ~${lineEq.effectiveCharsPerLine} chars/linha (letra ${describeHandwriting(lineEq.handwritingSize)})`,
    done: lineEq.maxLines == null
      ? `${lineEq.realized} linhas`
      : (
        <span class={linesWithin ? "" : "text-red-600 dark:text-red-400"}>
          {lineEq.realized} linhas (
          {linesWithin
            ? `dentro · sobraram ${lineEq.maxLines - lineEq.realized}`
            : `+${lineEq.realized - lineEq.maxLines} acima do limite`}
          )
        </span>
      )
  });

  if (maxValue > 0) {
    const realized =
      maxKind === "words" ? wordCount : maxKind === "chars" ? charCount : lineEq.realized;
    const within = realized <= maxValue;
    const unit =
      maxKind === "words" ? "palavras" : maxKind === "chars" ? "caracteres" : "linhas equivalentes";
    rows.push({
      label: `Limite extra (${unit})`,
      config: `${maxValue} ${unit}`,
      done: (
        <span class={within ? "" : "text-red-600 dark:text-red-400"}>
          {realized} ({within ? `dentro · ${maxValue - realized} sobram` : `+${realized - maxValue} acima`})
        </span>
      )
    });
  } else {
    rows.push({
      label: "Tamanho bruto",
      config: "Sem limite extra",
      done: `${wordCount} palavras · ${charCount} chars · ${lineCount} parágrafos`
    });
  }
  return (
    <div class="overflow-x-auto rounded-md border border-border">
      <table class="w-full border-collapse text-[11px]">
        <thead class="bg-muted/30">
          <tr>
            <th class="border border-border px-2 py-1.5 text-left font-bold uppercase tracking-wider">
              Critério
            </th>
            <th class="border border-border px-2 py-1.5 text-left font-bold uppercase tracking-wider">
              Configurado
            </th>
            <th class="border border-border px-2 py-1.5 text-left font-bold uppercase tracking-wider">
              Realizado
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td class="border border-border px-2 py-1.5 font-semibold">{r.label}</td>
              <td class="border border-border px-2 py-1.5 text-muted-foreground">
                {r.config}
              </td>
              <td class="border border-border px-2 py-1.5 font-semibold">{r.done}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GradeStrip({ grading }: { grading: NonNullable<AiStudySessionDetail["session"]["essayGrading"]> }) {
  const score = typeof grading.score === "number" ? grading.score : null;
  const status = grading.status ?? null;
  const statusKey = (status ?? "").toUpperCase();
  const tone =
    score == null
      ? "border-border bg-card/40 text-foreground"
      : statusKey === "EXCELENTE"
        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        : statusKey === "APROVADO"
          ? "border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300"
          : statusKey === "REGULAR"
            ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
            : "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300";
  return (
    <div class={`flex flex-wrap items-baseline justify-between gap-3 rounded-md border px-4 py-2.5 ${tone}`}>
      <div class="flex items-baseline gap-2">
        <span class="text-[10px] font-bold uppercase tracking-wider opacity-80">Nota</span>
        <span class="text-2xl font-extrabold tabular-nums">
          {score != null ? score.toFixed(2).replace(".", ",") : "—"}
        </span>
        <span class="text-xs font-semibold opacity-70">/ 10,00</span>
      </div>
      {status && (
        <span class="rounded-md bg-background/40 px-2 py-1 text-[10px] font-bold uppercase tracking-wider">
          {status}
        </span>
      )}
    </div>
  );
}

// =============================================================================
// Atomos compartilhados
// =============================================================================

function SectionCard({
  title,
  icon: Icon,
  children
}: {
  title: string;
  icon: any;
  children: any;
}) {
  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 p-3">
      <h3 class="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        <Icon className="size-3.5" /> {title}
      </h3>
      <div class="space-y-2">{children}</div>
    </section>
  );
}

function RadioPill({
  active,
  onClick,
  title,
  desc
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`flex-1 rounded-md border px-3 py-2 text-left text-xs transition-colors ${
        active
          ? "border-primary bg-primary/10"
          : "border-border bg-background hover:border-primary/50"
      }`}
    >
      <div class="font-bold">{title}</div>
      <div class="text-[10px] text-muted-foreground">{desc}</div>
    </button>
  );
}
